import React, { useState, useEffect, useCallback } from 'react';
import { FileSearch, Download, Eye, Upload, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import Navigation from './Navigation';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import { useApi } from '../hooks/useApi';

// Context types must match DocumentAccessLogService.ValidContextTypes on the backend
const CONTEXT_TYPES = [
    { value: '', label: 'All contexts' },
    { value: 'remediation-log', label: 'Remediation log' },
    { value: 'tprm-supporting-doc', label: 'TPRM supporting doc' },
    { value: 'evidence', label: 'Evidence' },
    { value: 'vendor-doc', label: 'Vendor document' },
];

// Actions must match DocumentAccessLogService.ValidActions on the backend
const ACTIONS = [
    { value: '', label: 'All actions' },
    { value: 'downloaded', label: 'Downloaded' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'uploaded', label: 'Uploaded' },
    { value: 'edited', label: 'Edited' },
    { value: 'deleted', label: 'Deleted' },
];

const ACTION_STYLES = {
    downloaded: { cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Download },
    viewed: { cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Eye },
    uploaded: { cls: 'bg-green-50 text-green-700 border-green-200', Icon: Upload },
    edited: { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Pencil },
    deleted: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: Trash2 },
};

const PAGE_SIZE = 25;

/**
 * Document Access audit view (UN EOI PR 6): who viewed/downloaded which document, when.
 * Route is RISK_MANAGER+ gated (RoleProtectedRoute in App.js) matching the backend
 * CanManageOrganizationRisks gate on GET /document-access-logs.
 */
const DocumentAccessLogPage = () => {
    const apiCall = useApi();

    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [contextType, setContextType] = useState('');
    const [action, setAction] = useState('');
    const [path, setPath] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const fetchLogs = useCallback(async (targetPage) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: String(targetPage), pageSize: String(PAGE_SIZE) });
            if (contextType) params.append('contextType', contextType);
            if (action) params.append('action', action);
            if (path) params.append('path', path);
            if (from) params.append('from', from);
            if (to) params.append('to', to);
            const data = await apiCall('GET', `/document-access-logs?${params.toString()}`);
            setLogs(data?.items || []);
            setTotalCount(data?.totalCount || 0);
            setPage(targetPage);
        } catch (err) {
            setError('Failed to load document access logs.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextType, action, path, from, to]);

    useEffect(() => {
        fetchLogs(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        const s = String(ts);
        const d = new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
        return d.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    const contextLabel = (value) =>
        CONTEXT_TYPES.find(c => c.value === value)?.label || value;

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center gap-3 mb-6">
                    <FileSearch className="h-7 w-7 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-content-primary">Document Access</h1>
                        <p className="text-sm text-content-secondary">
                            Audit trail of document activity — uploads, edits, deletions, downloads, and views — across remediation logs, evidence, and vendor documents.
                        </p>
                    </div>
                </div>

                <Card>
                    {/* Filters */}
                    <div className="flex flex-wrap items-end gap-3 mb-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Context</label>
                            <select
                                value={contextType}
                                onChange={(e) => setContextType(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                {CONTEXT_TYPES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
                            <select
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                {ACTIONS.map(a => (
                                    <option key={a.value} value={a.value}>{a.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Document path contains</label>
                            <input
                                type="text"
                                value={path}
                                onChange={(e) => setPath(e.target.value)}
                                placeholder="e.g. contract.pdf"
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <button
                            onClick={() => fetchLogs(1)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                        >
                            Apply
                        </button>
                    </div>

                    {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

                    {loading ? (
                        <div className="py-12 flex justify-center"><LoadingSpinner /></div>
                    ) : logs.length === 0 ? (
                        <div className="py-12 text-center text-sm text-gray-500">
                            No document access recorded yet for the selected filters.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                                        <th className="py-2 pr-4">When</th>
                                        <th className="py-2 pr-4">User</th>
                                        <th className="py-2 pr-4">Action</th>
                                        <th className="py-2 pr-4">Document</th>
                                        <th className="py-2 pr-4">Context</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">{formatTimestamp(log.timestamp)}</td>
                                            <td className="py-2.5 pr-4 font-medium text-content-primary">{log.userName}</td>
                                            <td className="py-2.5 pr-4">
                                                {(() => {
                                                    const { cls, Icon } = ACTION_STYLES[log.action] || ACTION_STYLES.viewed;
                                                    return (
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${cls}`}>
                                                            <Icon className="h-3 w-3" />
                                                            {log.action}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="py-2.5 pr-4 max-w-md">
                                                <div className="truncate font-medium text-content-primary" title={log.documentName}>{log.documentName}</div>
                                                <div className="truncate text-xs text-gray-400" title={log.documentPath}>{log.documentPath}</div>
                                            </td>
                                            <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                                {contextLabel(log.contextType)}
                                                {log.contextId && <span className="text-gray-400"> · {log.contextId}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && totalCount > 0 && (
                        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                            <span>{totalCount} record{totalCount === 1 ? '' : 's'}</span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => fetchLogs(page - 1)}
                                    disabled={page <= 1}
                                    className="p-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span>Page {page} of {totalPages}</span>
                                <button
                                    onClick={() => fetchLogs(page + 1)}
                                    disabled={page >= totalPages}
                                    className="p-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default DocumentAccessLogPage;
